import * as Sentry from '@sentry/nextjs'

import { useSendWebhookMutation } from '@/graphql/generated/hooks'
import type { SendWebhookMutation } from '@/graphql/generated/operations'
import { trackEvent } from '@/utils/analytics'

import {
  SEND_WEBHOOK_FAILURE,
  SEND_WEBHOOK_SEND,
  SEND_WEBHOOK_SUCCESS,
} from '../constants/analyticsEvents'

/** The destination's raw response, passed through unparsed. */
export type SendWebhookResponseI = NonNullable<
  SendWebhookMutation['sendWebhook']
>

interface UseSendWebhookResultIProps {
  isSending: boolean
  send: (webhookId: string) => Promise<SendWebhookResponseI | null>
}

/**
 * Sends an incident through a configured webhook (ENG-36019). The backend
 * renders the incident into the webhook's saved payload, makes the request
 * with the webhook's method and auth header, and returns the destination's
 * response as-is whatever its status — the mutation fails only when no
 * response is received (ENG-36027).
 *
 * Returns `null` when the mutation itself fails; SendWebhookModal surfaces
 * that inline in its response panel, so no toast fires here.
 */
export const useSendWebhook = (
  incidentId: string
): UseSendWebhookResultIProps => {
  const [sendWebhook, { loading: isSending }] = useSendWebhookMutation()

  const send = async (webhookId: string) => {
    trackEvent(SEND_WEBHOOK_SEND, {
      incident_id: incidentId,
      webhook_id: webhookId,
    })
    try {
      const { data } = await sendWebhook({
        variables: { input: { incidentId, webhookId } },
      })
      const response = data?.sendWebhook
      if (!response) {
        throw new Error('sendWebhook returned no payload')
      }
      trackEvent(SEND_WEBHOOK_SUCCESS, {
        incident_id: incidentId,
        webhook_id: webhookId,
        response_status: response.statusCode,
      })
      return response
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'send_manual_webhook',
          feature: 'incident_drawer',
          surface: 'send_webhook_modal',
        },
        extra: { incidentId, webhookId },
      })
      trackEvent(SEND_WEBHOOK_FAILURE, {
        incident_id: incidentId,
        webhook_id: webhookId,
      })
      return null
    }
  }

  return { isSending, send }
}
